import { useIntl, FormattedMessage } from 'react-intl';
import { history, useParams, useLocation } from 'umi';
import classnames from 'classnames';
import { useState, useMemo } from 'react';
import { useRequest } from 'ahooks';
import { Row, Col, Spin } from 'antd';
import { Button } from 'iglooform';
import numeral from 'numeral';
import { get } from 'lodash';
import moment from 'moment';
import FoldCard from '@/components/fold-card';
import ListItemPanel from '@/components/list-item-panel';
import ListItem from '@/components/list-item';
import AmountWithUnit from '@/components/amount-with-unit';
import PageFrame from '@/components/page-frame';
import ModalNote from '@/components/modal-note';
import Amount from '@/components/amount';
import useMobile from '@/hooks/useMobile';
import AddonList from '@/components/add-on';
import { isUndefined } from '@/utils/common';
import useUpdateRemoteLangMessage from '@/hooks/useUpdateRemoteLangMessage';
import useVehicleFiledsVisiblity from '@/hooks/useVehicleFiledsVisiblity';
import useHandleFormData from '@/hooks/useHandleFormData';
import specialIntlMessages from '@/utils/specialIntlMessages';
import premiumConfig from '@/configs/premium';
import { images } from '@/assets/images';
import {
  UpdateQuote,
  CreateShare,
  GetQuotation,
  GetProductDetail,
  GetPlanDetail,
  GetPlanCmsDetail,
  QuotationConfirm,
} from './service';
import './index.less';

const MotorSingleQuote = () => {
  const { query }: any = useLocation();
  const { type: productType } = useParams<{ type: TTypes }>();
  const { productKey, planKey, quotationId } = query;

  const { formatMessage } = useIntl();
  const isMobile = useMobile();

  const [noteVisible, setNoteVisible] = useState(false);
  const [addonModalVisible, setAddonModalVisible] = useState(false);

  const [quotationData, setQuotationData] = useState<any>({});
  const vehicleInfo = quotationData?.fast_quote;

  const { update } = useHandleFormData();
  const { loading: quotationLoading } = useRequest(GetQuotation, {
    onSuccess: (res) => {
      setQuotationData(res.quote);
    },
    defaultParams: [quotationId],
  });

  const { loading: productInfoLoading, data: productInfo } = useRequest(
    GetProductDetail,
    {
      defaultParams: [productType, productKey],
    },
  );

  const { loading: planInfoLoading, data: planInfo } = useRequest(
    GetPlanDetail,
    {
      defaultParams: [productType, productKey, planKey],
    },
  );

  const {
    loading: planCmsDetailLoading,
    data: planCmsDetail,
    run: getPlanCmsDetail,
  } = useRequest(GetPlanCmsDetail, {
    defaultParams: [productType, productKey, planKey],
  });
  const coverageType = planCmsDetail?.coverage_type ?? productType;

  const visibilities = useVehicleFiledsVisiblity({
    vehicleType: vehicleInfo?.vehicle_type,
    coverageType,
  });

  const { run: updateQuote, loading: updateQuoteLoading } = useRequest(
    UpdateQuote,
    {
      onSuccess: setQuotationData,
      manual: true,
    },
  );

  const { run: createShare } = useRequest(CreateShare, {
    manual: true,
    onSuccess: (res) => {
      const url =
        location.origin +
        `/home/${productType}/share?share_id=${
          res.share_id
        }&share_url=${encodeURIComponent(
          res.url,
        )}&replace_pathname=${encodeURIComponent('/home')}`;
      window.open(url);
    },
  });

  const { run: quotationConfirm, loading: updateStateLoading } = useRequest(
    QuotationConfirm,
    {
      manual: true,
      onSuccess: () => {
        history.replace({
          pathname: `/home/${productType}/application`,
          query: {
            productKey,
            planKey,
            quotationId,
          },
        });

        //event tracking: start_application
        window.gtag('event', 'start_application', {
          page: 'fast_quote',
          product_key: productKey,
          plan_key: planKey,
          quote_id: quotationId,
          user_unique_id: JSON.parse(localStorage.getItem('user_info') ?? '{}')
            ?.id,
        });
      },
    },
  );

  const noteInitValue = useMemo(
    () => ({
      note: quotationData?.fast_quote?.note,
    }),
    [quotationData?.fast_quote?.note],
  );

  useUpdateRemoteLangMessage([
    () => getPlanCmsDetail(productType, productKey, planKey),
  ]);

  const updateQuotetation = ({
    note,
    includePa,
  }: {
    note?: string;
    includePa?: boolean;
  }) => {
    const updateJson: { [prop: string]: any } = {};
    if (typeof note !== 'undefined') {
      updateJson['note'] = note;
    }
    if (typeof includePa !== 'undefined') {
      updateJson['include_pa'] = includePa ? 'Yes' : 'No';
    }
    updateQuote(productType, productKey, planKey, quotationId, {
      fast_quote: update(updateJson, quotationData?.fast_quote),
    });
  };

  if (
    productInfoLoading ||
    quotationLoading ||
    planCmsDetailLoading ||
    planInfoLoading
  )
    return null;

  const insurer = planCmsDetail?.cms?.data?.attributes?.insurer;
  const logoUrl =
    insurer?.data?.attributes?.logo?.data?.attributes?.url ||
    productInfo?.details?.logo_url;
  const planName =
    planCmsDetail?.cms?.data?.attributes?.planName || productInfo?.name;

  const specificPremiumConfig = premiumConfig[productType][coverageType];

  const premiumInfo: { [prop: string]: any } = quotationData?.meta;
  const basicPremium = get(premiumInfo, specificPremiumConfig?.basicPremium);

  const paPremium = get(premiumInfo, specificPremiumConfig?.paPremium, 0);
  const vatPremium = get(premiumInfo, specificPremiumConfig?.vatPremium, null);
  const grossPremium = numeral(basicPremium).add(paPremium).value() as number;
  const amountPayable = get(
    premiumInfo,
    specificPremiumConfig?.amountPayable,
    0,
  );

  const fastNote = quotationData?.fast_quote?.note;
  const includePa = quotationData?.fast_quote?.include_pa === 'Yes';

  const cardTitleMaps = {
    motor:
      planCmsDetail?.soi_category === 'motorcycle'
        ? formatMessage({
            id: 'Motorcycle Details',
          })
        : formatMessage({
            id: 'Vehicle Details',
          }),
    travel: formatMessage({
      id: 'Travel Details',
    }),
    pa: formatMessage({
      id: 'Policy Details',
    }),
    health: formatMessage({
      id: 'Policy Details',
    }),
  };
  const iconMaps = {
    motor:
      planCmsDetail?.soi_category === 'motorcycle'
        ? images.motorcycle_icon
        : images.vehicle_icon,
    travel: images.travel_icon,
    pa: images.pa_icon,
    health: images.hc_icon,
  };

  const foldCardList = [
    // motor
    {
      label: formatMessage({ id: 'Vehicle Type' }),
      value: vehicleInfo?.vehicle_type ? (
        <FormattedMessage {...specialIntlMessages[vehicleInfo?.vehicle_type]} />
      ) : null,
      hidden: isUndefined(vehicleInfo?.vehicle_type),
    },
    {
      label: formatMessage({ id: 'Business Usage' }),
      value: vehicleInfo?.business_usage ? (
        <FormattedMessage
          {...specialIntlMessages[vehicleInfo?.business_usage]}
        />
      ) : null,
      hidden: !visibilities.business,
    },
    {
      label: formatMessage({ id: 'Number of Seats' }),
      value: vehicleInfo?.seats_number,
      hidden: !visibilities.numberOfSeas,
    },
    {
      label: formatMessage({ id: 'Tonnage' }),
      value: vehicleInfo?.tonnage,
      hidden: !visibilities.tonnage,
    },
    {
      label: formatMessage({ id: 'Vehicle Value' }),
      value: (
        <AmountWithUnit
          amount={numeral(vehicleInfo?.vehicle_value ?? 0).value() as number}
          unit={formatMessage({ id: 'VND' })}
        />
      ),
      hidden: isUndefined(vehicleInfo?.vehicle_value),
    },
    {
      label: formatMessage({ id: 'Vehicle Origin' }),
      value: vehicleInfo?.vehicle_origin ? (
        <FormattedMessage
          {...specialIntlMessages[vehicleInfo?.vehicle_origin]}
        />
      ) : null,
      hidden: isUndefined(vehicleInfo?.vehicle_origin),
    },
    {
      label: formatMessage({ id: 'Vehicle Manufacture Date' }),
      value: vehicleInfo?.manufacture_date
        ? moment(vehicleInfo?.manufacture_date).format('DD / MM / YYYY')
        : undefined,
      hidden:
        isUndefined(vehicleInfo?.manufacture_date) ||
        vehicleInfo?.manufacture_date === 0,
    },
    {
      label: formatMessage({ id: 'Vehicle Registration Date' }),
      value: vehicleInfo?.registration_date
        ? moment(vehicleInfo?.registration_date).format('MM / YYYY')
        : undefined,
      hidden:
        isUndefined(vehicleInfo?.registration_date) ||
        vehicleInfo?.registration_date === 0,
    },
    // travel
    {
      label: formatMessage({ id: 'Destination' }),
      value: vehicleInfo?.destination ? (
        <FormattedMessage {...specialIntlMessages[vehicleInfo?.destination]} />
      ) : null,
      hidden: isUndefined(vehicleInfo?.destination),
    },
    {
      label: formatMessage({ id: 'Travel Start Date' }),
      value: vehicleInfo?.travel_start_date
        ? moment(vehicleInfo?.travel_start_date).format('DD / MM / YYYY')
        : undefined,
    },
    {
      label: formatMessage({ id: 'Travel End Date' }),
      value: vehicleInfo?.travel_end_date
        ? moment(vehicleInfo?.travel_end_date).format('DD / MM / YYYY')
        : undefined,
    },
    // hospital cash
    {
      label: formatMessage({ id: 'Policy Duration' }),
      value: vehicleInfo?.policy_duration ? (
        <FormattedMessage
          {...specialIntlMessages[vehicleInfo?.policy_duration]}
        />
      ) : null,
      hidden: isUndefined(vehicleInfo?.policy_duration),
    },
    {
      label: formatMessage({ id: 'No. of Insured' }),
      value: vehicleInfo?.insured_num ? vehicleInfo?.insured_num : null,
      hidden: isUndefined(vehicleInfo?.insured_num),
    },
    // pa
    {
      label: formatMessage({ id: 'Plan' }),
      value: vehicleInfo?.applicants ? (
        <FormattedMessage {...specialIntlMessages[vehicleInfo?.applicants]} />
      ) : null,
      hidden: isUndefined(vehicleInfo?.applicants),
    },
  ].filter((e) => !isUndefined(e.value) && !e.hidden);

  const premiumList: any[] = [
    {
      label: formatMessage({ id: 'Basic Premium' }),
      value: <Amount amount={basicPremium} />,
    },
    {
      label: formatMessage({ id: 'Add-ons Sub-total' }),
      value: <Amount amount={paPremium} />,
      children:
        paPremium > 0
          ? [
              {
                label: formatMessage({ id: 'Personal Accident' }),
                value: <Amount amount={paPremium} />,
              },
            ]
          : [],
      hidden:
        coverageType === 'cpd' ||
        ['travel', 'health', 'pa'].includes(productType),
    },
    {
      label: formatMessage({ id: 'Total Gross Premium' }),
      value: <Amount amount={grossPremium} />,
      type: 'strong',
    },
    {
      label: formatMessage({ id: 'VAT(10%)' }),
      value: <Amount amount={vatPremium} />,
      hidden: vatPremium === null,
    },
    {
      label: formatMessage({ id: 'Amount Payable' }),
      value: <Amount amount={amountPayable} />,
      type: 'total',
    },
  ].filter((e) => !e.hidden) as any[];
  const benefits = planCmsDetail?.cms?.data?.attributes?.benefits ?? [];

  const benefitsFieldList = benefits.map((item: { [prop: string]: any }) => {
    return {
      label: item?.text,
      value: item?.coverage,
      children: item.subItems.map((e: { [prop: string]: any }) => {
        return {
          label: e.text,
          value: e?.coverage,
        };
      }),
    };
  });

  const showCoverageColumn = benefitsFieldList.some((item: any) => {
    return item.value || item?.children?.some((e: any) => e.value);
  });

  const planInfoList = [
    {
      label: formatMessage({ id: 'Benefit' }),
      type: 'strong',
      value: showCoverageColumn ? formatMessage({ id: 'Coverage' }) : null,
    },
    ...benefitsFieldList,
    {
      label: formatMessage({ id: 'Add-ons Benefits' }),
      children: includePa
        ? planCmsDetail?.soi_category === 'motorcycle'
          ? [
              {
                label: formatMessage({
                  id: 'Personal Accident',
                }),
                value: formatMessage({
                  id: 'Up to VND 10.000.000',
                }),
              },
            ]
          : [
              {
                label: formatMessage({
                  id: 'Personal Accident (Passenger)',
                }),
                value: formatMessage({
                  id: 'Up to VND 150.000.000',
                }),
              },
              {
                label: formatMessage({
                  id: 'Personal Accident (Driver)',
                }),
                value: formatMessage({
                  id: 'Up to VND 10.000.000',
                }),
              },
            ]
        : [],
      labelAfter: (
        <Button type="link" onClick={() => setAddonModalVisible(true)}>
          {formatMessage({ id: 'Modify' })}
        </Button>
      ),
      style: { marginTop: 12 },
      hidden:
        coverageType === 'cpd' ||
        ['travel', 'health', 'pa'].includes(productType),
    },
  ].filter((e) => !e.hidden);

  if (!quotationData?.fast_quote)
    return (
      <PageFrame title="">
        <></>
      </PageFrame>
    );

  return (
    <PageFrame>
      <div
        className={classnames([
          'confirmationContainer',
          {
            'is-mobile': isMobile,
          },
        ])}
      >
        <FoldCard
          noExtendIcon={false}
          title={cardTitleMaps[productType]}
          titleIconSrc={iconMaps[productType]}
          list={foldCardList}
        />

        <Spin spinning={updateStateLoading || updateQuoteLoading}>
          <Row className="priceInfo" gutter={[24, 24]}>
            <Col span={isMobile ? 24 : 12}>
              <ListItem icon={logoUrl} title={planName} list={planInfoList} />

              <Row
                className="discountBox"
                justify="space-between"
                align="middle"
                gutter={[0, 8]}
              >
                <Col>
                  <span className="discountTitle">
                    {formatMessage({ id: 'Add Note' })}
                  </span>
                </Col>
                <Col>
                  <Button type="link" onClick={() => setNoteVisible(true)}>
                    {formatMessage({ id: 'Modify' })}
                  </Button>
                </Col>
                {fastNote && (
                  <Col span={24} className="discountNote">
                    {fastNote}
                  </Col>
                )}
              </Row>
            </Col>

            <Col span={isMobile ? 24 : 12}>
              <ListItemPanel
                title={formatMessage({
                  id: formatMessage({ id: 'Premium Details' }),
                })}
                subTitle={formatMessage({
                  id: formatMessage({ id: 'All premiums in Vietnamese Dong' }),
                })}
                list={premiumList}
              />
            </Col>
          </Row>
        </Spin>

        <Row className="bottomButton" gutter={[24, 24]}>
          <Col {...(isMobile ? { span: 24 } : {})}>
            <Button
              type="primary"
              onClick={() => {
                quotationConfirm(productType, productKey, planKey, quotationId);
              }}
            >
              {formatMessage({ id: 'Complete Application' })}
            </Button>
          </Col>
          <Col {...(isMobile ? { span: 24 } : {})}>
            <Button
              onClick={() => {
                createShare(productType, productKey, planKey, quotationId);

                //event tracking: share quote
                window.gtag('event', 'share_quote', {
                  page: 'fast_quote',
                  channel: 'link',
                  quote_id: quotationId,
                  product_key: quotationData?.product?.product_key,
                  plan_key: quotationData?.plan?.plan_key,
                  user_unique_id: JSON.parse(
                    localStorage.getItem('user_info') ?? '{}',
                  )?.id,
                });
              }}
            >
              {formatMessage({ id: 'Share' })}
            </Button>
          </Col>
        </Row>

        <AddonList
          visible={addonModalVisible}
          onCancel={() => setAddonModalVisible(false)}
          addonList={[
            {
              add_on_id: 1,
              enable: includePa,
              option: {},
            },
          ]}
          addonOptions={{}}
          onConfirm={(newAddOn) => {
            if (newAddOn.length < 1) return;
            updateQuotetation({ includePa: newAddOn[0]?.enable });
          }}
        />

        <ModalNote
          initValue={noteInitValue}
          visible={noteVisible}
          onCancel={() => setNoteVisible(false)}
          onConfirm={(values) => {
            updateQuotetation({ note: values?.note });
          }}
        />
      </div>
    </PageFrame>
  );
};

export default MotorSingleQuote;
